import React, { useRef, useState } from "react";
import { colors, radius, shadows, spacing, typography } from "./tokens";

const baseStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.xs,
  minHeight: 38,
  padding: `${spacing.md}px ${spacing.xl}px`,
  borderRadius: radius.md,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: colors.border,
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
  lineHeight: typography.lineHeight.compact,
  cursor: "pointer",
  transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease",
  boxShadow: "none",
  background: colors.surface,
  color: colors.text,
};

const variantStyles = {
  primary: {
    default: {
      background: colors.brandPrimary,
      borderColor: colors.brandPrimary,
      color: colors.onAccent,
      boxShadow: shadows.xs,
    },
    hover: {
      background: colors.brandHover,
      borderColor: colors.brandHover,
      boxShadow: shadows.sm,
    },
    active: {
      background: colors.brandActive,
      borderColor: colors.brandActive,
      boxShadow: shadows.xs,
    },
  },
  secondary: {
    default: {
      background: colors.surface,
      borderColor: colors.border,
      color: colors.textPrimary,
    },
    hover: {
      background: colors.surfaceSubtle,
      borderColor: colors.textSubtle,
    },
    active: {
      background: colors.surfaceMuted,
      borderColor: colors.borderStrong,
    },
  },
  ghost: {
    default: {
      background: "transparent",
      borderColor: "transparent",
      color: colors.textSecondary,
      boxShadow: "none",
    },
    hover: {
      background: colors.surfaceSubtle,
      color: colors.textPrimary,
    },
    active: {
      background: colors.surfaceMuted,
      color: colors.textPrimary,
    },
  },
  destructive: {
    default: {
      background: colors.danger,
      borderColor: colors.danger,
      color: colors.onAccent,
      boxShadow: shadows.xs,
    },
    hover: {
      background: colors.dangerHover,
      borderColor: colors.dangerHover,
      boxShadow: shadows.sm,
    },
    active: {
      background: colors.dangerActive,
      borderColor: colors.dangerActive,
    },
  },
  danger: {
    default: {
      background: colors.danger,
      borderColor: colors.danger,
      color: colors.onAccent,
      boxShadow: shadows.xs,
    },
    hover: {
      background: colors.dangerHover,
      borderColor: colors.dangerHover,
      boxShadow: shadows.sm,
    },
    active: {
      background: colors.dangerActive,
      borderColor: colors.dangerActive,
    },
  },
};

const disabledBaseStyle = {
  background: colors.disabledBg,
  borderColor: colors.disabledBorder,
  color: colors.disabledText,
  cursor: "not-allowed",
  boxShadow: "none",
  opacity: 1,
};

const ghostDisabledStyle = {
  background: "transparent",
  borderColor: "transparent",
  color: colors.disabledText,
};

const resolveVariantStateStyle = (variant, hovered, pressed) => {
  const resolved = variantStyles[variant] || variantStyles.primary;
  return {
    ...resolved.default,
    ...(hovered ? resolved.hover : null),
    ...(pressed ? resolved.active : null),
  };
};

const resolveFocusVisible = (event, pointerDown) => {
  const target = event?.target;
  if (target && typeof target.matches === "function") {
    try {
      return target.matches(":focus-visible");
    } catch (_err) {
      // Fallback for environments without :focus-visible selector support.
    }
  }
  return !pointerDown;
};

const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    disabled = false,
    loading = false,
    type = "button",
    children,
    style,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [focusVisible, setFocusVisible] = useState(false);
  const pointerDownRef = useRef(false);

  const selectedVariant = resolveVariantStateStyle(variant, hovered && !isDisabled, pressed && !isDisabled);
  const focusShadow = selectedVariant.boxShadow
    ? `${selectedVariant.boxShadow}, 0 0 0 3px ${colors.focusRing}`
    : `0 0 0 3px ${colors.focusRing}`;

  const mergedStyle = {
    ...baseStyle,
    ...selectedVariant,
    ...(!isDisabled && focusVisible
      ? { boxShadow: focusShadow }
      : null),
    ...(isDisabled ? disabledBaseStyle : null),
    ...(isDisabled && variant === "ghost" ? ghostDisabledStyle : null),
    ...style,
  };

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      data-variant={variant}
      style={mergedStyle}
      ref={ref}
      onMouseEnter={(event) => {
        setHovered(true);
        onMouseEnter && onMouseEnter(event);
      }}
      onMouseLeave={(event) => {
        setHovered(false);
        setPressed(false);
        onMouseLeave && onMouseLeave(event);
      }}
      onMouseDown={(event) => {
        if (event.button === 0) {
          setPressed(true);
          pointerDownRef.current = true;
        }
        onMouseDown && onMouseDown(event);
      }}
      onMouseUp={(event) => {
        setPressed(false);
        pointerDownRef.current = false;
        onMouseUp && onMouseUp(event);
      }}
      onFocus={(event) => {
        const isFocusVisible = resolveFocusVisible(event, pointerDownRef.current);
        setFocusVisible(Boolean(isFocusVisible));
        onFocus && onFocus(event);
      }}
      onBlur={(event) => {
        setPressed(false);
        setFocusVisible(false);
        pointerDownRef.current = false;
        onBlur && onBlur(event);
      }}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          setPressed(true);
        }
        pointerDownRef.current = false;
        onKeyDown && onKeyDown(event);
      }}
      onKeyUp={(event) => {
        if (event.key === " " || event.key === "Enter") {
          setPressed(false);
        }
        onKeyUp && onKeyUp(event);
      }}
      {...rest}
    >
      {loading ? "Loading..." : children}
    </button>
  );
});

export default Button;
